import { useEffect, useState, type FormEvent } from 'react';
import { Building2, Save, Settings2, ShieldCheck } from 'lucide-react';
import { PageHeader } from '../Components/data/PageHeader';
import { Field } from '../Components/forms/Field';
import { Button } from '../Components/ui/Button';
import { Card, CardHead } from '../Components/ui/Card';
import { Input } from '../Components/ui/Input';
import { Select } from '../Components/ui/Select';
import { useAuth } from '../contexts/AuthContext';
import NexusLayout from '../Layouts/NexusLayout';
import api from '../services/api';

type ConfigItem = { chave: string; valor: string | number | boolean };

type ConfigState = {
    responsavel_tecnico: string;
    crf_responsavel: string;
    anvisa_usuario: string;
    anvisa_ambiente: string;
};

const INITIAL_CONFIG: ConfigState = {
    responsavel_tecnico: '',
    crf_responsavel: '',
    anvisa_usuario: '',
    anvisa_ambiente: 'PRODUCAO',
};

export default function Configuracoes() {
    const { empresa } = useAuth();
    const [config, setConfig] = useState(INITIAL_CONFIG);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        api.get<ConfigItem[]>('/configuracoes', { signal: controller.signal })
            .then((response) => {
                const values = Object.fromEntries(
                    response.data.map((item) => [item.chave, String(item.valor ?? '')]),
                );
                setConfig((current) => ({ ...current, ...values }));
            })
            .catch((requestError: unknown) => {
                if (!controller.signal.aborted) {
                    console.error('Erro ao carregar configurações:', requestError);
                    setMessage({ tone: 'error', text: 'Não foi possível carregar as configurações.' });
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, []);

    function update<K extends keyof ConfigState>(key: K, value: ConfigState[K]) {
        setConfig((current) => ({ ...current, [key]: value }));
    }

    async function save(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setSaving(true);
        setMessage(null);

        try {
            await Promise.all(
                Object.entries(config).map(([chave, valor]) =>
                    api.post('/configuracoes', {
                        chave,
                        valor,
                        tipo: 'STRING',
                        grupo: chave.startsWith('anvisa_') ? 'ANVISA' : 'SNGPC',
                    }),
                ),
            );
            setMessage({ tone: 'success', text: 'Configurações salvas com sucesso.' });
        } catch (requestError) {
            console.error('Erro ao salvar configurações:', requestError);
            setMessage({ tone: 'error', text: 'Não foi possível salvar as configurações.' });
        } finally {
            setSaving(false);
        }
    }

    return (
        <NexusLayout title="Configurações">
            <div className="nx-page">
                <PageHeader
                    icon={<Settings2 size={21} strokeWidth={2} />}
                    title="Configurações do SNGPC"
                    description="Revise os dados da farmácia, do responsável técnico e da integração ANVISA."
                />

                <form className="nx-sngpc-settings" onSubmit={save}>
                    <Card>
                        <CardHead
                            title={
                                <span className="nx-sngpc-card-title">
                                    <Building2 size={17} /> Dados da farmácia
                                </span>
                            }
                        />
                        <div className="nx-form-row nx-form-row--2">
                            <Field label="Razão social" htmlFor="empresa-razao-social">
                                <Input value={empresa?.razao_social ?? ''} readOnly disabled />
                            </Field>
                            <Field label="CNPJ" htmlFor="empresa-cnpj">
                                <Input value={empresa?.cnpj ?? ''} readOnly disabled />
                            </Field>
                            <Field label="Responsável técnico" htmlFor="responsavel-tecnico">
                                <Input
                                    value={config.responsavel_tecnico}
                                    onChange={(event) => update('responsavel_tecnico', event.target.value)}
                                    placeholder="Nome do farmacêutico"
                                    disabled={loading}
                                />
                            </Field>
                            <Field label="CRF do responsável" htmlFor="crf-responsavel">
                                <Input
                                    value={config.crf_responsavel}
                                    onChange={(event) => update('crf_responsavel', event.target.value)}
                                    placeholder="Número e UF"
                                    disabled={loading}
                                />
                            </Field>
                        </div>
                    </Card>

                    <Card>
                        <CardHead
                            title={
                                <span className="nx-sngpc-card-title">
                                    <ShieldCheck size={17} /> Integração ANVISA
                                </span>
                            }
                        />
                        <div className="nx-form-row nx-form-row--2">
                            <Field label="Usuário SNGPC" htmlFor="anvisa-usuario">
                                <Input
                                    value={config.anvisa_usuario}
                                    onChange={(event) => update('anvisa_usuario', event.target.value)}
                                    placeholder="Usuário de acesso"
                                    autoComplete="off"
                                    disabled={loading}
                                />
                            </Field>
                            <Field label="Ambiente" htmlFor="anvisa-ambiente">
                                <Select
                                    value={config.anvisa_ambiente}
                                    onChange={(event) => update('anvisa_ambiente', event.target.value)}
                                    disabled={loading}
                                >
                                    <option value="PRODUCAO">Produção</option>
                                    <option value="HOMOLOGACAO">Homologação</option>
                                </Select>
                            </Field>
                        </div>
                    </Card>

                    {message ? (
                        <div className={`nx-sngpc-message is-${message.tone}`} role="status">
                            {message.text}
                        </div>
                    ) : null}

                    <div className="nx-sngpc-settings-actions">
                        <Button type="submit" loading={saving} loadingText="Salvando..." disabled={loading}>
                            <Save size={16} />
                            Salvar configurações
                        </Button>
                    </div>
                </form>
            </div>
        </NexusLayout>
    );
}
